/**
 * The ISCO-08 coding for one role, shown plainly.
 *
 * This is the receipt behind the claim that a career here is classified rather
 * than invented: the four-digit unit group when we hold one, the ILO hierarchy
 * above it, the skill level it implies, and — honestly — how the code was
 * matched. A similar-title match is labelled as such instead of being dressed
 * up as exact, and a role we have only placed in a major group says so.
 *
 * Display only. Nothing here feeds scoring.
 */
import { useEffect, useState } from "react";
import { Landmark, GraduationCap } from "lucide-react";
import { fetchOccupationCoding, type OccupationCoding as Coding } from "@/lib/careerContent";
import {
  ISCO08_MATCH_METHODS,
  ISCO08_SKILL_LEVELS,
  confidencePhrase,
} from "@/lib/occupationStandards";

const LEVEL_LABELS: Record<number, string> = {
  1: "Major group",
  2: "Sub-major group",
  3: "Minor group",
  4: "Unit group",
};

export function OccupationCoding({
  ciId,
  className = "",
}: {
  ciId: string;
  className?: string;
}) {
  const [coding, setCoding] = useState<Coding | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCoding(null);
    // A missing code is not an error worth shouting about: the panel simply
    // does not render, and the rest of the page is unaffected.
    fetchOccupationCoding(ciId)
      .then((c) => {
        if (!cancelled) setCoding(c);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [ciId]);

  if (!coding || !coding.hierarchy.length) return null;

  const method = ISCO08_MATCH_METHODS[coding.isco08_match_method ?? ""];
  const level = coding.isco08_skill_level
    ? ISCO08_SKILL_LEVELS[coding.isco08_skill_level]
    : null;
  const confidence = confidencePhrase(coding.isco08_match_confidence);
  const unit = coding.isco08_unit_group;

  return (
    <section
      aria-labelledby={`isco-coding-${coding.ci_id}`}
      className={`rounded-3xl border border-border bg-card p-5 md:p-6 ${className}`}
    >
      <div className="flex items-start gap-3">
        <Landmark className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0">
          <h3
            id={`isco-coding-${coding.ci_id}`}
            className="font-display text-sm font-semibold text-foreground"
          >
            How this role is classified
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {unit
              ? `ISCO-08 unit group ${unit} — the ILO's four-digit code for this kind of work.`
              : "We hold the broad family for this role, not a four-digit code yet."}
          </p>
        </div>
      </div>

      <ol className="mt-4 space-y-1.5">
        {coding.hierarchy.map((g) => (
          <li key={g.code} className="flex items-baseline gap-2 text-sm">
            <span className="w-14 shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
              {g.code}
            </span>
            <span className="min-w-0 text-foreground/90">{g.title}</span>
            <span className="ml-auto shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground">
              {LEVEL_LABELS[g.level] ?? `Level ${g.level}`}
            </span>
          </li>
        ))}
      </ol>

      {level && (
        <div className="mt-4 flex items-start gap-2 rounded-2xl border border-border/60 bg-muted/20 p-3">
          <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <p className="text-xs leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground">{level.short}.</span> {level.meaning}
          </p>
        </div>
      )}

      {coding.definition && (
        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{coding.definition}</p>
      )}

      {method && (
        <p className="mt-4 border-t border-border/60 pt-3 text-xs leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">
            {method.label}
            {confidence ? ` · ${confidence}` : ""}.
          </span>{" "}
          {method.meaning}
        </p>
      )}
    </section>
  );
}

export default OccupationCoding;
